import React, { useState, useEffect } from 'react';
import { Check, X, Plus, User, FileText, Calendar, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Task } from '../../lib/types';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { useSettingsStore } from '../../lib/stores/settingsStore';

interface TaskCompletionDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (taskId: string, outcome: string, createNew?: boolean) => Promise<void>;
  task: Task | null;
}

export function TaskCompletionDrawer({ isOpen, onClose, onComplete, task }: TaskCompletionDrawerProps) {
  const [outcome, setOutcome] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingNew, setIsSubmittingNew] = useState(false);
  const { settings } = useSettingsStore();
  const outcomeRequired = settings?.activity_outcome_required !== false;

  // Check if task has context for creating new activities (deal_id or client_id)
  const hasContextForNewActivity = Boolean(task?.deal_id || task?.client_id || task?.lead_id);

  // Translations
  const t = {
    taskModal: {
      title: "Completa attività",
      outcomeLabel: "Inserisci l'esito dell'attività",
      outcomePlaceholder: "Descrivi il risultato dell'attività...",
      cancel: "Annulla",
      saving: "Salvataggio...",
      saveOutcome: "Salva esito",
      processing: "Elaborazione...",
      saveAndCreateNew: "Salva e crea nuova"
    }
  };

  // Reset outcome when drawer opens with new task
  useEffect(() => {
    if (isOpen) {
      setOutcome('');
    }
  }, [isOpen, task?.id]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    // Handle body scroll locking when drawer is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleSubmit = async (e: React.FormEvent | React.MouseEvent, createNew: boolean = false) => {
    e.preventDefault();
    if (!task || (outcomeRequired && !outcome.trim())) return;

    if (createNew) {
      setIsSubmittingNew(true);
    } else {
      setIsSubmitting(true);
    }

    try {
      await onComplete(task.id, outcome, createNew);
      setOutcome('');
      onClose();
    } catch (error) {
      console.error('Failed to complete task:', error);
    } finally {
      setIsSubmitting(false);
      setIsSubmittingNew(false);
    }
  };

  const handleCloseDrawer = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <>
          {/* Overlay with fade animation */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-[100]"
            onClick={handleCloseDrawer}
          />

          {/* Drawer with slide animation */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed inset-y-0 right-0 w-full sm:w-2/3 lg:w-1/2 xl:w-1/3 bg-white dark:bg-[#2F3437] shadow-xl z-[101] flex flex-col border-l border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]"
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <span className="text-sm font-medium text-[#37352F] dark:text-white">
                    {t.taskModal.title}
                  </span>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-500 dark:text-white/70 hover:text-gray-700 dark:hover:text-white transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4">
                {/* Task Summary */}
                {task && (
                  <div className="mb-4 p-3 bg-[#F7F7F5] dark:bg-[#373C3F] border border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] rounded-md">
                    <h4 className="text-sm font-medium text-[#37352F] dark:text-white mb-2 flex items-center">
                      <FileText className="h-4 w-4 mr-2 text-[#787774] dark:text-gray-400 flex-shrink-0" />
                      <span className="truncate">{task.title}</span>
                    </h4>
                    <div className="space-y-1.5 text-xs text-[#787774] dark:text-gray-400">
                      {(task.client_name || task.clients?.business_name) && (
                        <div className="flex items-center">
                          <User className="h-3 w-3 mr-2 flex-shrink-0" />
                          <span>Cliente: <span className="text-[#37352F] dark:text-white">{task.client_name || task.clients?.business_name}</span></span>
                        </div>
                      )}
                      {task.deal_title && (
                        <div className="flex items-center">
                          <FileText className="h-3 w-3 mr-2 flex-shrink-0" />
                          <span>Offerta: <span className="text-[#37352F] dark:text-white">{task.deal_title}</span></span>
                          {task.deal_amount && (
                            <span className="ml-1 font-medium text-[#37352F] dark:text-white">
                              ({new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(task.deal_amount)})
                            </span>
                          )}
                        </div>
                      )}
                      {task.due_date && (
                        <div className="flex items-center">
                          <Calendar className="h-3 w-3 mr-2 flex-shrink-0" />
                          <span>Scadenza: <span className="text-[#37352F] dark:text-white">{format(parseISO(task.due_date), 'dd/MM/yyyy', { locale: it })}</span></span>
                          <Clock className="h-3 w-3 ml-3 mr-1 flex-shrink-0" />
                          <span className="text-[#37352F] dark:text-white">{format(parseISO(task.due_date), 'HH:mm')}</span>
                        </div>
                      )}
                      {task.description && (
                        <div className="mt-2 pt-2 border-t border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
                          <div
                            className="text-xs text-[#787774] dark:text-gray-400 line-clamp-2"
                            dangerouslySetInnerHTML={{ __html: task.description }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Outcome Form */}
                <form onSubmit={(e) => handleSubmit(e)} className="space-y-4">
                  <div>
                    <label htmlFor="outcome" className="block text-xs font-medium text-[#787774] dark:text-gray-400 mb-1">
                      {t.taskModal.outcomeLabel}
                    </label>
                    <textarea
                      id="outcome"
                      rows={6}
                      className="block w-full px-3 py-2 text-sm bg-[#F7F7F5] dark:bg-[#373C3F] border border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] rounded-md text-[#37352F] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#2383E2] focus:border-[#2383E2] resize-none placeholder:text-[#787774] dark:placeholder:text-gray-500"
                      placeholder={t.taskModal.outcomePlaceholder}
                      value={outcome}
                      onChange={(e) => setOutcome(e.target.value)}
                      required={outcomeRequired}
                    />
                  </div>
                </form>
              </div>

              {/* Footer with actions */}
              <div className="border-t border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] px-4 py-3 bg-[#F7F7F5] dark:bg-[#373C3F]">
                <div className="flex items-center justify-end space-x-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-3 py-1.5 text-xs font-medium text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.04] rounded-md transition-colors"
                  >
                    {t.taskModal.cancel}
                  </button>
                  <button
                    type="submit"
                    onClick={(e) => handleSubmit(e)}
                    disabled={isSubmitting || isSubmittingNew || (outcomeRequired && !outcome.trim())}
                    className={`flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                      isSubmitting || (outcomeRequired && !outcome.trim())
                        ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                        : 'bg-[#37352F] text-white hover:bg-[#2F2E2B] dark:bg-white dark:text-[#37352F] dark:hover:bg-gray-200'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="animate-spin mr-1.5">⏳</span>
                        {t.taskModal.saving}
                      </>
                    ) : (
                      <>
                        <Check className="h-3.5 w-3.5 mr-1.5" />
                        Salva
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleSubmit(e, true)}
                    disabled={isSubmitting || isSubmittingNew || (outcomeRequired && !outcome.trim())}
                    className={`flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                      isSubmittingNew || (outcomeRequired && !outcome.trim())
                        ? 'bg-[#2383E2]/50 text-white/70 cursor-not-allowed'
                        : 'bg-[#2383E2] text-white hover:bg-[#1a6bc2]'
                    }`}
                    title={hasContextForNewActivity
                      ? "Salva questa attività e crea una nuova attività correlata"
                      : "Salva e vai a selezionare dove creare la prossima attività"
                    }
                  >
                    {isSubmittingNew ? (
                      <>
                        <span className="animate-spin mr-1.5">⏳</span>
                        {t.taskModal.processing}
                      </>
                    ) : (
                      <>
                        <Plus className="h-3.5 w-3.5 mr-1.5" />
                        Salva e Nuova
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
